import React from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Container, Spinner } from 'react-bootstrap';
import ScanResults from '../components/ScanResults';
import { FaceScanAPIResponse, SkinScanAPIResponse, UserDemographics } from '../types';

const VIEW_FACESCAN_RESULTS_WEBHOOK_URL = 'https://aibot.futurisk.in/webhook/7ecaa14e-b609-4c9d-bc67-ee97f7c92e7e';

interface StoredFaceScanWebhookRecord {
  UserName?: string;
  Phone?: string;
  Dob?: string;
  Gender?: 'male' | 'female';
  HeightCm?: number | string | null;
  WeightKg?: number | string | null;
  Email?: string;
  Age?: number | string | null;
  FaceScanJson?: string | FaceScanAPIResponse | null;
  SkinScanJson?: string | SkinScanAPIResponse | null;
}

type ViewFaceScanResultsData = React.ComponentProps<typeof ScanResults>['data'];

const decodeBase64Value = (value: string) => {
  const normalizedValue = value.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength = normalizedValue.length % 4;
  const paddedValue = paddingLength === 0
    ? normalizedValue
    : `${normalizedValue}${'='.repeat(4 - paddingLength)}`;

  return atob(paddedValue);
};

const parseJsonValue = <T,>(value: string | T | null | undefined) => {
  if (!value) {
    return null;
  }

  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    return null;
  }
};

const toNumber = (value: number | string | null | undefined) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  return 0;
};

const decodeScanId = (value: string | null) => {
  if (!value) {
    return null;
  }

  const normalizedValue = value.replace(/ /g, '+');
  const candidates = [normalizedValue];

  try {
    const decodedUriValue = decodeURIComponent(normalizedValue);

    if (decodedUriValue !== normalizedValue) {
      candidates.push(decodedUriValue);
    }
  } catch (error) {
    // Ignore malformed URI encoding and keep base candidate.
  }

  for (const candidate of candidates) {
    try {
      return decodeBase64Value(candidate);
    } catch (error) {
      continue;
    }
  }

  return null;
};

const ViewFaceScanResultsPage: React.FC = () => {
  const location = useLocation();
  const [scanResults, setScanResults] = React.useState<ViewFaceScanResultsData | null>(null);
  const [userData, setUserData] = React.useState<UserDemographics | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const scanId = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return decodeScanId(params.get('scanid'));
  }, [location.search]);

  React.useEffect(() => {
    if (!scanId) {
      setScanResults(null);
      setUserData(null);
      setError('Missing or invalid scan id.');
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();

    const loadScanResults = async () => {
      setIsLoading(true);
      setError(null);

      const response = await fetch(VIEW_FACESCAN_RESULTS_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scanid: scanId }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to load scan results (${response.status})`);
      }

      const payload = await response.json() as StoredFaceScanWebhookRecord[] | StoredFaceScanWebhookRecord | null;
      const storedRecord = Array.isArray(payload) ? payload[0] : payload;

      if (!storedRecord) {
        throw new Error('No scan results were returned for this scan id.');
      }

      const faceScanResult = parseJsonValue<FaceScanAPIResponse>(storedRecord.FaceScanJson);
      const skinScanResult = parseJsonValue<SkinScanAPIResponse>(storedRecord.SkinScanJson);

      if (!faceScanResult) {
        throw new Error('Stored face scan data is missing or invalid.');
      }

      const normalizedOriginalVitals = faceScanResult.original_vitals
        ? {
            ...faceScanResult.original_vitals,
            THRR: Array.isArray(faceScanResult.original_vitals.THRR)
              ? faceScanResult.original_vitals.THRR.join(' - ')
              : faceScanResult.original_vitals.THRR,
          }
        : undefined;

      const combinedResults: ViewFaceScanResultsData = {
        ...faceScanResult,
        original_vitals: normalizedOriginalVitals,
        skinScan: skinScanResult || undefined,
      };

      const mappedUserData: UserDemographics = {
        name: storedRecord.UserName || 'Guest',
        phone: storedRecord.Phone || '',
        dob: storedRecord.Dob || '',
        gender: storedRecord.Gender === 'female' ? 'female' : 'male',
        age: toNumber(storedRecord.Age),
        height: toNumber(storedRecord.HeightCm),
        weight: toNumber(storedRecord.WeightKg),
        email: storedRecord.Email || undefined,
      };

      setScanResults(combinedResults);
      setUserData(mappedUserData);
      setIsLoading(false);
    };

    loadScanResults().catch((loadError: Error) => {
      if (loadError.name === 'AbortError') {
        return;
      }

      setScanResults(null);
      setUserData(null);
      setError(loadError.message || 'Unable to load scan results.');
      setIsLoading(false);
    });

    return () => {
      controller.abort();
    };
  }, [scanId]);

  if (isLoading) {
    return (
      <Container className='py-10 text-center'>
        <Spinner animation='border' role='status' />
      </Container>
    );
  }

  if (error || !scanResults) {
    return (
      <Container className='py-10'>
        <Alert variant='danger' className='mb-0'>
          {error || 'Unable to load scan results.'}
        </Alert>
      </Container>
    );
  }

  return <ScanResults data={scanResults} userData={userData} />;
};

export default ViewFaceScanResultsPage;